import React, { useEffect, useMemo, useState } from "react";
import ScrollToTop from "../ScrollToTop";
import AdminSidebar from "../../Component/AdminSidebar";
import {
  formatAdminPrice,
  loadCatalog,
  LOW_STOCK_THRESHOLD,
} from "../../data/catalog";
import {
  adjustInventoryStock,
  breakdownLot,
  deleteInventoryLot,
  loadInventoryLots,
  loadInventoryMovements,
  saveInventoryLot,
  setInventoryStock,
} from "../../data/inventory";
import { printLotLabel } from "../../data/labels";
import { computeMargin } from "../../data/taxRates";

const emptyLotForm = {
  id: "",
  lotCode: "",
  quantity: "",
  unitType: "piece",
  supplierName: "",
  receivedDate: "",
  expiryDate: "",
  notes: "",
  purchase_price_ht: "",
  supplier_invoice_ref: "",
};

const STOCK_PAGE_SIZE = 12;
const QUICK_STOCK_ACTIONS = [
  {
    delta: -1,
    label: "-1",
    className: "btn btn-sm btn-light border text-dark",
  },
  {
    delta: 5,
    label: "+5",
    className: "btn btn-sm btn-success-subtle border border-success text-success",
  },
  {
    delta: 10,
    label: "+10",
    className: "btn btn-sm btn-success-subtle border border-success text-success",
  },
];

const getStockState = (product) => {
  const stock = Number(product?.stock || 0);
  const reserved = Number(product?.reservedStock || 0);
  const available = Math.max(0, stock - reserved);

  if (stock <= 0) {
    return {
      key: "out",
      label: "Rupture",
      badgeClass: "bg-secondary",
      alertText: "Produit en rupture",
      available,
    };
  }

  if (stock <= LOW_STOCK_THRESHOLD) {
    return {
      key: "low",
      label: "A reapprovisionner",
      badgeClass: "bg-danger",
      alertText: "Alerte stock faible",
      available,
    };
  }

  return {
    key: "ok",
    label: "OK",
    badgeClass: "bg-success",
    alertText: available <= LOW_STOCK_THRESHOLD ? "Disponible limite" : "Stock sain",
    available,
  };
};

const MyAcconutNotification = () => {
  const [loaderStatus, setLoaderStatus] = useState(true);
  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [lots, setLots] = useState([]);
  const [selectedLotProductId, setSelectedLotProductId] = useState("");
  const [lotForm, setLotForm] = useState(emptyLotForm);
  const [lowOnly, setLowOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [stockFilter, setStockFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [breakdownMode, setBreakdownMode] = useState(false);
  const [breakdownParentLot, setBreakdownParentLot] = useState(null);
  const [breakdownChildCode, setBreakdownChildCode] = useState("");
  const [breakdownChildQty, setBreakdownChildQty] = useState("");
  const [breakdownChildUnit, setBreakdownChildUnit] = useState("piece");
  const [breakdownChildNotes, setBreakdownChildNotes] = useState("");
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [activeTab, setActiveTab] = useState("stock");
  const [movementDateFrom, setMovementDateFrom] = useState("");
  const [movementDateTo, setMovementDateTo] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchProducts = async () => {
      const [loadedProducts, loadedMovements] = await Promise.all([
        loadCatalog(),
        loadInventoryMovements(200),
      ]);
      if (!isMounted) {
        return;
      }
      setProducts(loadedProducts);
      setMovements(loadedMovements);
      setSelectedLotProductId(loadedProducts[0]?.id || "");
      setLoaderStatus(false);
    };

    fetchProducts();

    return () => {
      isMounted = false;
    };
  }, []);

  const stats = useMemo(() => {
    const lowStock = products.filter(
      (product) => product.stock <= LOW_STOCK_THRESHOLD
    ).length;
    const outOfStock = products.filter((product) => product.stock <= 0).length;
    const reservedUnits = products.reduce(
      (sum, product) => sum + Number(product.reservedStock || 0),
      0
    );
    const totalUnits = products.reduce(
      (sum, product) => sum + Number(product.stock || 0),
      0
    );
    const expiringSoon = lots.filter((lot) => lot.expiresSoon).length;
    const expiredLots = lots.filter((lot) => lot.isExpired).length;

    return { lowStock, outOfStock, reservedUnits, totalUnits, expiringSoon, expiredLots };
  }, [lots, products]);

  const filteredMovements = useMemo(() => {
    let result = movements;
    if (movementDateFrom) {
      result = result.filter((m) => (m.createdAt || "").slice(0, 10) >= movementDateFrom);
    }
    if (movementDateTo) {
      result = result.filter((m) => (m.createdAt || "").slice(0, 10) <= movementDateTo);
    }
    return result;
  }, [movements, movementDateFrom, movementDateTo]);

  useEffect(() => {
    let isMounted = true;

    const fetchLots = async () => {
      if (!selectedLotProductId) {
        setLots([]);
        return;
      }
      const loadedLots = await loadInventoryLots(selectedLotProductId);
      if (isMounted) {
        setLots(loadedLots);
      }
    };

    fetchLots();

    return () => {
      isMounted = false;
    };
  }, [selectedLotProductId]);

  const visibleProducts = useMemo(() => {
    const sorted = [...products].sort((a, b) => {
      const stateDiff =
        (getStockState(a).key === "out" ? 0 : getStockState(a).key === "low" ? 1 : 2) -
        (getStockState(b).key === "out" ? 0 : getStockState(b).key === "low" ? 1 : 2);

      if (stateDiff !== 0) {
        return stateDiff;
      }

      return Number(a.stock || 0) - Number(b.stock || 0);
    });

    const normalizedSearch = searchTerm.trim().toLowerCase();

    return sorted.filter((product) => {
      const state = getStockState(product);
      const matchesLowOnly = !lowOnly || state.key !== "ok";
      const matchesFilter = stockFilter === "all" || state.key === stockFilter;
      const matchesSearch =
        !normalizedSearch ||
        [
          product.name,
          product.category,
          product.sku,
          product.barcode,
          product.id,
          product.type,
        ]
          .join(" ")
          .toLowerCase()
          .includes(normalizedSearch);

      return matchesLowOnly && matchesFilter && matchesSearch;
    });
  }, [lowOnly, products, searchTerm, stockFilter]);

  const totalPages = Math.max(1, Math.ceil(visibleProducts.length / STOCK_PAGE_SIZE));

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * STOCK_PAGE_SIZE;
    return visibleProducts.slice(start, start + STOCK_PAGE_SIZE);
  }, [currentPage, visibleProducts]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, stockFilter, lowOnly]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const refreshInventoryData = async (nextProducts) => {
    setProducts(nextProducts);
    setMovements(await loadInventoryMovements(200));
    if (selectedLotProductId) {
      setLots(await loadInventoryLots(selectedLotProductId));
    }
  };

  const adjustStock = async (id, delta) => {
    try {
      const nextProducts = await adjustInventoryStock(
        id,
        delta,
        `Ajustement manuel ${delta > 0 ? "+" : ""}${delta}`
      );
      await refreshInventoryData(nextProducts);
      setMessage("Stock mis a jour.");
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.message || "Impossible d'ajuster le stock.");
      setMessage("");
    }
  };

  const setExactStock = async (id, value) => {
    try {
      const nextProducts = await setInventoryStock(id, value, "Correction manuelle");
      await refreshInventoryData(nextProducts);
      setMessage("Stock corrige.");
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.message || "Impossible de corriger le stock.");
      setMessage("");
    }
  };

  const handleLotChange = (event) => {
    const { name, value } = event.target;
    setLotForm((current) => ({
      ...current,
      [name]: value,
    }));
    setMessage("");
    setErrorMessage("");
  };

  const resetLotForm = () => {
    setLotForm(emptyLotForm);
  };

  const handleLotSubmit = async (event) => {
    event.preventDefault();
    if (!selectedLotProductId) {
      return;
    }

    await saveInventoryLot(selectedLotProductId, lotForm);
    setLots(await loadInventoryLots(selectedLotProductId));
    setMessage("Lot enregistre.");
    resetLotForm();
  };

  const handleBreakdown = async () => {
    if (!breakdownParentLot || !breakdownChildCode || !breakdownChildQty) return;
    try {
      await breakdownLot(breakdownParentLot.id, [
        { lotCode: breakdownChildCode, quantity: breakdownChildQty, notes: breakdownChildNotes }
      ], breakdownChildUnit);
      setLots(await loadInventoryLots(selectedLotProductId));
      setMessage("Lot decoupe avec succes.");
      setBreakdownMode(false);
      setBreakdownParentLot(null);
      setBreakdownChildCode("");
      setBreakdownChildQty("");
      setBreakdownChildNotes("");
    } catch (err) {
      setMessage("Erreur: " + err.message);
    }
  };

  const handleLotDelete = async (lotId) => {
    await deleteInventoryLot(lotId);
    setLots(await loadInventoryLots(selectedLotProductId));
    setMessage("Lot supprime.");
  };

  return (
    <div>
      <ScrollToTop />
      <section className="admin-section">
        <div className="container">
          <div className="row">
            <AdminSidebar activePath="/MyAcconutNotification" />

            <div className="col-lg-9 col-md-8 col-12">
              {loaderStatus ? (
                <div className="p-6 p-lg-10">
                  <div className="alert alert-light border mb-0">
                    Chargement du stock...
                  </div>
                </div>
              ) : (
                <div className="p-6 p-lg-10">
                  <div className="d-flex justify-content-between align-items-center mb-6 flex-wrap gap-2">
                    <div>
                      <h2 className="mb-1">Gestion du stock</h2>
                      <p className="text-muted mb-0">
                        Ajustez les quantites en direct. La boutique client
                        relit automatiquement ces stocks.
                      </p>
                    </div>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="lowOnly"
                        checked={lowOnly}
                        onChange={(event) => setLowOnly(event.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="lowOnly">
                        Voir seulement le stock faible
                      </label>
                    </div>
                  </div>

                  <div className="admin-tabs">
                    {[
                      { key: "stock", label: "Table de stock" },
                      { key: "lots", label: "Lots" },
                      { key: "historique", label: "Historique" },
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        className={`admin-tab-btn${activeTab === tab.key ? " active" : ""}`}
                        onClick={() => setActiveTab(tab.key)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {message ? (
                    <div className="alert alert-success mb-4">{message}</div>
                  ) : null}
                  {errorMessage ? (
                    <div className="alert alert-danger mb-4">{errorMessage}</div>
                  ) : null}

                  {activeTab === "stock" && (<>
                    <div className="row g-4 mb-8">
                    <div className="col-md-4">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body p-4">
                          <p className="text-muted mb-2">References</p>
                          <h3 className="mb-0">{products.length}</h3>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body p-4">
                          <p className="text-muted mb-2">Stock faible</p>
                          <h3 className="mb-0">{stats.lowStock}</h3>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body p-4">
                          <p className="text-muted mb-2">Ruptures</p>
                          <h3 className="mb-0">{stats.outOfStock}</h3>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body p-4">
                          <p className="text-muted mb-2">Stock reserve</p>
                          <h3 className="mb-0">{stats.reservedUnits}</h3>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body p-4">
                          <p className="text-muted mb-2">Lots a surveiller</p>
                          <h3 className="mb-0">{stats.expiringSoon}</h3>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body p-4">
                          <p className="text-muted mb-2">Lots expires</p>
                          <h3 className="mb-0">{stats.expiredLots}</h3>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card border-0 shadow-sm mb-8">
                    <div className="card-body p-4">
                      <h5 className="mb-3">Pilotage rapide</h5>
                      <div className="alert alert-light border mb-0">
                        Seuil critique actuel :{" "}
                        <strong>{LOW_STOCK_THRESHOLD} unites</strong>. Total en
                        stock : <strong>{stats.totalUnits} unites</strong>.
                      </div>
                    </div>
                  </div>

                  <div className="card border-0 shadow-sm">
                    <div className="card-body p-4">
                      <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-4">
                        <div>
                          <h5 className="mb-1">Table de stock</h5>
                          <small className="text-muted">
                            {visibleProducts.length} reference(s) trouvee(s), page {currentPage} / {totalPages}
                          </small>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                          <input
                            type="search"
                            className="form-control"
                            style={{ minWidth: "250px" }}
                            placeholder="Scanner ou rechercher produit, SKU, code-barres..."
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                          />
                          <select
                            className="form-select"
                            value={stockFilter}
                            onChange={(event) => setStockFilter(event.target.value)}
                            style={{ minWidth: "180px" }}
                          >
                            <option value="all">Tous les etats</option>
                            <option value="out">Ruptures</option>
                            <option value="low">Alertes stock faible</option>
                            <option value="ok">Stock OK</option>
                          </select>
                        </div>
                      </div>

                      <div className="alert alert-light border d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
                        <span>
                          Recherche rapide, pagination et alertes actives pour rendre la liste exploitable sur un grand catalogue.
                        </span>
                        <span className="text-muted">
                          Le champ de recherche accepte aussi le scan direct du code-barres produit.
                        </span>
                      </div>

                      <div className="table-responsive">
                        <table className="table mb-0 align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Produit</th>
                              <th>Code-barres / Ref.</th>
                              <th>Categorie</th>
                              <th>Type</th>
                              <th>Prix</th>
                              <th>Stock</th>
                              <th>Reserve</th>
                              <th>Disponible</th>
                              <th>Etat</th>
                              <th>Ajuster</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedProducts.map((product) => {
                              const stockState = getStockState(product);

                              return (
                                <tr key={product.id}>
                                  <td>
                                    <div className="fw-semibold">
                                      {product.name}
                                    </div>
                                    <small className="text-muted">
                                      {product.id}
                                    </small>
                                  </td>
                                  <td>
                                    <div
                                      className="fw-semibold"
                                      style={{
                                        fontFamily: "monospace",
                                        letterSpacing: "0.08em",
                                      }}
                                    >
                                      {product.barcode || product.sku || "Non renseigne"}
                                    </div>
                                    <small className="text-muted">
                                      Ref: {product.sku || "Aucune"} · {stockState.alertText}
                                    </small>
                                  </td>
                                  <td>{product.category}</td>
                                  <td className="text-uppercase">
                                    {product.type}
                                  </td>
                                  <td>{formatAdminPrice(product.price)}</td>
                                  <td style={{ minWidth: "110px" }}>
                                    <input
                                      type="number"
                                      min="0"
                                      className="form-control form-control-sm"
                                      value={product.stock}
                                      onChange={(event) =>
                                        setExactStock(
                                          product.id,
                                          event.target.value
                                        )
                                      }
                                    />
                                  </td>
                                  <td>{Number(product.reservedStock || 0)}</td>
                                  <td>{stockState.available}</td>
                                  <td>
                                    <span className={`badge ${stockState.badgeClass}`}>
                                      {stockState.label}
                                    </span>
                                  </td>
                                  <td style={{ minWidth: "190px" }}>
                                    <div className="d-flex flex-column gap-2">
                                      <small className="text-muted fw-semibold">
                                        Actions rapides
                                      </small>
                                      <div
                                        className="d-inline-flex align-items-center gap-2 flex-wrap"
                                        style={{ rowGap: "0.5rem" }}
                                      >
                                        {QUICK_STOCK_ACTIONS.map((action) => (
                                          <button
                                            key={`${product.id}-${action.delta}`}
                                            type="button"
                                            className={action.className}
                                            style={{
                                              minWidth: action.delta === 10 ? "58px" : "50px",
                                              borderRadius: "12px",
                                              fontWeight: 600,
                                              letterSpacing: "0.01em",
                                            }}
                                            onClick={() => adjustStock(product.id, action.delta)}
                                          >
                                            {action.label}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                            {!paginatedProducts.length ? (
                              <tr>
                                <td colSpan="10" className="text-muted text-center py-4">
                                  Aucun produit ne correspond a cette recherche.
                                </td>
                              </tr>
                            ) : null}
                          </tbody>
                        </table>
                      </div>
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-4">
                        <small className="text-muted">
                          Affichage {(visibleProducts.length && (currentPage - 1) * STOCK_PAGE_SIZE + 1) || 0}
                          {" - "}
                          {Math.min(currentPage * STOCK_PAGE_SIZE, visibleProducts.length)} sur {visibleProducts.length}
                        </small>
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-dark btn-sm"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                          >
                            Precedent
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-dark btn-sm"
                            disabled={currentPage === totalPages}
                            onClick={() =>
                              setCurrentPage((page) => Math.min(totalPages, page + 1))
                            }
                          >
                            Suivant
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  </>)}

                  {activeTab === "lots" && (
                  <div className="row g-4 mt-1">
                    <div className="col-lg-5">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body p-4">
                          <div className="d-flex justify-content-between align-items-center mb-4">
                            <h5 className="mb-0">Lots & expiration</h5>
                            <button
                              type="button"
                              className="btn btn-outline-dark btn-sm"
                              onClick={resetLotForm}
                            >
                              Nouveau lot
                            </button>
                          </div>
                          <form onSubmit={handleLotSubmit}>
                            <div className="row g-3">
                              <div className="col-12">
                                <label className="form-label">Produit</label>
                                <select
                                  className="form-select"
                                  value={selectedLotProductId}
                                  onChange={(event) => setSelectedLotProductId(event.target.value)}
                                >
                                  <option value="">Choisir un produit</option>
                                  {products.map((product) => (
                                    <option key={product.id} value={product.id}>
                                      {product.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="col-12">
                                <label className="form-label">Code lot</label>
                                <input
                                  name="lotCode"
                                  className="form-control"
                                  value={lotForm.lotCode}
                                  onChange={handleLotChange}
                                  placeholder="LOT-FRAIS-001"
                                  required
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Quantite</label>
                                <input
                                  name="quantity"
                                  type="number"
                                  step="0.001"
                                  className="form-control"
                                  value={lotForm.quantity}
                                  onChange={handleLotChange}
                                  required
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Date d'expiration</label>
                                <input
                                  name="expiryDate"
                                  type="date"
                                  className="form-control"
                                  value={lotForm.expiryDate}
                                  onChange={handleLotChange}
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Unite</label>
                                <select name="unitType" className="form-select" value={lotForm.unitType} onChange={handleLotChange}>
                                  <option value="piece">Piece</option>
                                  <option value="kg">Kilogramme</option>
                                  <option value="g">Gramme</option>
                                  <option value="l">Litre</option>
                                </select>
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Fournisseur</label>
                                <input name="supplierName" className="form-control" value={lotForm.supplierName} onChange={handleLotChange} placeholder="Nom du fournisseur" />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Prix d'achat HT (EUR)</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  name="purchase_price_ht"
                                  className="form-control"
                                  placeholder="0.00"
                                  value={lotForm.purchase_price_ht || ""}
                                  onChange={(e) => setLotForm({ ...lotForm, purchase_price_ht: e.target.value })}
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Reference facture fournisseur</label>
                                <input
                                  name="supplier_invoice_ref"
                                  className="form-control"
                                  placeholder="ex: FA-2026-001"
                                  value={lotForm.supplier_invoice_ref || ""}
                                  onChange={(e) => setLotForm({ ...lotForm, supplier_invoice_ref: e.target.value })}
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Date de reception</label>
                                <input name="receivedDate" type="date" className="form-control" value={lotForm.receivedDate} onChange={handleLotChange} />
                              </div>
                              <div className="col-12">
                                <label className="form-label">Notes</label>
                                <input name="notes" className="form-control" value={lotForm.notes} onChange={handleLotChange} placeholder="Notes sur le lot..." />
                              </div>
                              <div className="col-12">
                                <button className="btn btn-primary" type="submit" disabled={!selectedLotProductId}>
                                  Enregistrer le lot
                                </button>
                              </div>
                            </div>
                          </form>
                          <div className="mt-4 pt-3 border-top">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h6 className="mb-0">Decouper un lot</h6>
                              <button type="button" className="btn btn-outline-warning btn-sm" onClick={() => { setBreakdownMode(!breakdownMode); setBreakdownParentLot(null); }}>
                                {breakdownMode ? "Annuler" : "Decouper"}
                              </button>
                            </div>
                            {breakdownMode && (
                              <>
                                <div className="mb-2">
                                  <label className="form-label small">Lot parent</label>
                                  <select className="form-select form-select-sm" value={breakdownParentLot?.id || ""} onChange={(e) => {
                                    const lot = lots.find((l) => l.id === e.target.value);
                                    setBreakdownParentLot(lot || null);
                                  }}>
                                    <option value="">Choisir un lot parent...</option>
                                    {lots.filter((l) => Number(l.quantity) > 0).map((l) => (
                                      <option key={l.id} value={l.id}>{l.lotCode} — {l.quantity} {l.unitType}</option>
                                    ))}
                                  </select>
                                </div>
                                {breakdownParentLot && (
                                  <div className="row g-2">
                                    <div className="col-12">
                                      <label className="form-label small">Code lot enfant</label>
                                      <input className="form-control form-control-sm" value={breakdownChildCode} onChange={(e) => setBreakdownChildCode(e.target.value)} placeholder={`${breakdownParentLot.lotCode}-F1`} />
                                    </div>
                                    <div className="col-6">
                                      <label className="form-label small">Quantite</label>
                                      <input type="number" step="0.001" className="form-control form-control-sm" value={breakdownChildQty} onChange={(e) => setBreakdownChildQty(e.target.value)} />
                                    </div>
                                    <div className="col-6">
                                      <label className="form-label small">Unite</label>
                                      <select className="form-select form-select-sm" value={breakdownChildUnit} onChange={(e) => setBreakdownChildUnit(e.target.value)}>
                                        <option value="piece">Piece</option>
                                        <option value="kg">Kilogramme</option>
                                        <option value="g">Gramme</option>
                                        <option value="l">Litre</option>
                                      </select>
                                    </div>
                                    <div className="col-12">
                                      <label className="form-label small">Notes</label>
                                      <input className="form-control form-control-sm" value={breakdownChildNotes} onChange={(e) => setBreakdownChildNotes(e.target.value)} placeholder="Notes optionnelles" />
                                    </div>
                                    <div className="col-12">
                                      <small className="text-muted">Reste apres decoupe: {breakdownParentLot.quantity - Number(breakdownChildQty || 0)} {breakdownParentLot.unitType}</small>
                                    </div>
                                    <div className="col-12">
                                      <button className="btn btn-warning btn-sm" onClick={handleBreakdown} disabled={!breakdownChildCode || !breakdownChildQty || Number(breakdownChildQty) <= 0}>
                                        Decouper
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-7">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body p-4">
                          <h5 className="mb-4">Lots du produit</h5>
                          {lots.length > 0 && (() => {
                            const lotsWithPurchase = lots.filter((l) => l.purchasePriceHt > 0 && (products.find((p) => p.id === l.productId)?.price || 0) > 0);
                            if (lotsWithPurchase.length === 0) return null;
                            const avgMargin = lotsWithPurchase.reduce((sum, l) => sum + (computeMargin(products.find((p) => p.id === l.productId)?.price || 0, l.purchasePriceHt) || 0), 0) / lotsWithPurchase.length;
                            const totalPurchase = lotsWithPurchase.reduce((sum, l) => sum + l.purchasePriceHt * l.quantity, 0);
                            const totalSelling = lotsWithPurchase.reduce((sum, l) => sum + (products.find((p) => p.id === l.productId)?.price || 0) * l.quantity, 0);
                            return (
                              <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                  <div className="card border-0 shadow-sm bg-light">
                                    <div className="card-body py-2 px-3">
                                      <small className="text-muted">Marge moyenne</small>
                                      <h5 className="mb-0">{avgMargin.toFixed(1)}%</h5>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-md-4">
                                  <div className="card border-0 shadow-sm bg-light">
                                    <div className="card-body py-2 px-3">
                                      <small className="text-muted">Valeur achat totale</small>
                                      <h5 className="mb-0">{totalPurchase.toFixed(2)} EUR</h5>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-md-4">
                                  <div className="card border-0 shadow-sm bg-light">
                                    <div className="card-body py-2 px-3">
                                      <small className="text-muted">Valeur vente totale</small>
                                      <h5 className="mb-0">{totalSelling.toFixed(2)} EUR</h5>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                          <div className="table-responsive">
                            <table className="table mb-0 align-middle">
                              <thead className="table-light">
                                <tr>
                                  <th>Lot</th>
                                  <th>Qte</th>
                                  <th>Fournisseur</th>
                                  <th>Expiration</th>
                                  <th>Etat</th>
                                  <th className="text-end">Prix achat</th>
                                  <th className="text-end">Marge</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {lots.map((lot) => {
                                  const lotProductPrice = products.find((p) => p.id === lot.productId)?.price || 0;
                                  return (
                                  <tr key={lot.id}>
                                    <td>
                                      {lot.lotCode}
                                      {lot.parentLotId && (
                                        <small className="d-block text-muted">Parent: {lot.parentLotId}</small>
                                      )}
                                      {lot.notes && (
                                        <small className="d-block text-muted" title={lot.notes} style={{maxWidth:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',display:'block'}}>{lot.notes}</small>
                                      )}
                                    </td>
                                    <td>
                                      {lot.quantity} {lot.unitType}
                                    </td>
                                    <td>{lot.supplierName || "-"}</td>
                                    <td>{lot.expiryDate || "-"}</td>
                                    <td>
                                      <span
                                        className={`badge ${
                                          lot.isExpired
                                            ? "bg-danger"
                                            : lot.expiresSoon
                                            ? "bg-warning text-dark"
                                            : "bg-success"
                                        }`}
                                      >
                                        {lot.isExpired
                                          ? "Expire"
                                          : lot.expiresSoon
                                          ? "Bientot"
                                          : "OK"}
                                      </span>
                                      {lot.daysUntilExpiry !== null ? (
                                        <small className="d-block text-muted">
                                          {lot.daysUntilExpiry >= 0
                                            ? `${lot.daysUntilExpiry} jour(s)`
                                            : `${Math.abs(lot.daysUntilExpiry)} jour(s) depasses`}
                                        </small>
                                      ) : null}
                                    </td>
                                    <td className="text-end">{lot.purchasePriceHt > 0 ? `${lot.purchasePriceHt.toFixed(2)} EUR` : "-"}</td>
                                    <td className="text-end">
                                      {lot.purchasePriceHt > 0 && lotProductPrice > 0
                                        ? (() => {
                                            const margin = computeMargin(lotProductPrice, lot.purchasePriceHt);
                                            return margin !== null ? (
                                              <span className={`badge ${margin > 20 ? "bg-success" : margin > 10 ? "bg-warning text-dark" : "bg-danger"}`}>
                                                {margin}%
                                              </span>
                                            ) : "-";
                                          })()
                                        : "-"}
                                    </td>
                                    <td>
                                      <div className="d-flex gap-1 flex-wrap">
                                        <button
                                          type="button"
                                          className="btn btn-outline-secondary btn-sm"
                                          onClick={() => printLotLabel(lot, products.find((p) => p.id === selectedLotProductId)?.name)}
                                        >
                                          Imprimer
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-outline-danger btn-sm"
                                          onClick={() => handleLotDelete(lot.id)}
                                        >
                                          Supprimer
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                                })}
                                {!lots.length ? (
                                  <tr>
                                    <td colSpan="8" className="text-muted">
                                      Aucun lot enregistre pour ce produit.
                                    </td>
                                  </tr>
                                ) : null}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}

                  {activeTab === "historique" && (
                  <div className="card border-0 shadow-sm mt-8">
                    <div className="card-body p-4">
                      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <h5 className="mb-0">Historique des mouvements</h5>
                        <small className="text-muted">
                          {filteredMovements.length} mouvement{filteredMovements.length !== 1 ? "s" : ""}
                        </small>
                      </div>
                      <div className="row g-2 mb-3">
                        <div className="col-auto">
                          <label className="form-label" style={{ fontSize: 12 }}>Du</label>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={movementDateFrom}
                            onChange={(e) => setMovementDateFrom(e.target.value)}
                          />
                        </div>
                        <div className="col-auto">
                          <label className="form-label" style={{ fontSize: 12 }}>Au</label>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={movementDateTo}
                            onChange={(e) => setMovementDateTo(e.target.value)}
                          />
                        </div>
                        {(movementDateFrom || movementDateTo) && (
                          <div className="col-auto d-flex align-items-end">
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => { setMovementDateFrom(""); setMovementDateTo(""); }}
                            >
                              Effacer
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="table-responsive">
                        <table className="table mb-0 align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Date</th>
                              <th>Produit</th>
                              <th>Mouvement</th>
                              <th>Quantite</th>
                              <th>Reference</th>
                              <th>Raison</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredMovements.map((movement) => (
                              <tr key={movement.id}>
                                <td>{movement.createdAtLabel}</td>
                                <td>{movement.productName}</td>
                                <td>
                                  <span className="badge bg-light text-dark border">
                                    {movement.movementLabel}
                                  </span>
                                </td>
                                <td>
                                  {movement.quantity} {movement.unitType}
                                </td>
                                <td>
                                  {movement.referenceLabel}
                                  {movement.referenceId ? (
                                    <small className="d-block text-muted">
                                      {movement.referenceId}
                                    </small>
                                  ) : null}
                                </td>
                                <td>{movement.reason || "-"}</td>
                              </tr>
                            ))}
                            {!filteredMovements.length ? (
                              <tr>
                                <td colSpan="6" className="text-muted">
                                  Aucun mouvement pour cette periode.
                                </td>
                              </tr>
                            ) : null}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MyAcconutNotification;
